import axiosInstance from './axiosInstance';
import {
  AppointmentDataResponseDto,
  AppointmentDataCreationDto,
  AppointmentHistoryResponseDto,
  AllergyResponseDto,
  DiseaseResponseDto,
  MedicalBackgroundResponseDto,
  FamilyHistoryCreationDto,
  FamilyHistoryResponseDto,
  SurgeryDataResponseDto,
  SurgeryCreationDto,
  SurgeryResponseDto,
  SymptomResponseDto,
  CommonEntitiesListResponse,
  HealthMetricResponse,
} from '@docbay/schemas';
import { PaginationPropsWithSearch } from 'common/types/pagination';

/**
 * API Service for Consultation Data management.
 */
export const ConsultationDataAPI = {
  /**
   * Retrieves consultation data for a given appointment ID.
   *
   * @param {string} appointmentId - The ID of the appointment to retrieve consultation data for.
   * @returns {Promise<AppointmentDataResponseDto>} A promise that resolves with the consultation data response.
   */
  getConsultationData: (appointmentId: string) =>
    axiosInstance.get<AppointmentDataResponseDto>(
      `/appointment-data/${appointmentId}`,
    ),

  /**
   * Retrieves consultation data history for a given appointment ID.
   *
   * @param {string} appointmentId - The ID of the appointment to retrieve consultation data history for.
   * @returns {Promise<AppointmentHistoryResponseDto[]>} A promise that resolves with an array of consultation data history responses.
   */
  getConsultationDataHistory: (appointmentId: string) =>
    axiosInstance.get<AppointmentHistoryResponseDto[]>(
      `/appointment-data/history/${appointmentId}`,
    ),

  /**
   * Retrieves the medical background of a patient by their ID.
   *
   * @param {string} patientId - The ID of the patient to retrieve medical background for.
   * @returns {Promise<MedicalBackgroundResponseDto>} A promise that resolves with the medical background response.
   */
  getPatientMedicalBackground: (patientId: string) =>
    axiosInstance.get<MedicalBackgroundResponseDto>(
      `/medical-background/patients/${patientId}`,
    ),

  /**
   * Retrieves a list of allergies based on pagination and search parameters.
   *
   * @param {PaginationPropsWithSearch} params - The pagination and search parameters.
   * @returns {Promise<CommonEntitiesListResponse<AllergyResponseDto>>} A promise that resolves with a response containing a list of allergies.
   */
  getAllergies: (params: PaginationPropsWithSearch) =>
    axiosInstance.get<CommonEntitiesListResponse<AllergyResponseDto>>(
      '/medical-background/allergies',
      { params },
    ),

  /**
   * Retrieves a list of diseases based on pagination and search parameters.
   *
   * @param {PaginationPropsWithSearch} params - The pagination and search parameters.
   * @returns {Promise<CommonEntitiesListResponse<DiseaseResponseDto>>} A promise that resolves with a response containing a list of diseases.
   */
  getDiseases: (params: PaginationPropsWithSearch) =>
    axiosInstance.get<CommonEntitiesListResponse<DiseaseResponseDto>>(
      '/medical-background/diseases',
      { params },
    ),

  /**
   * Retrieves a list of surgeries based on pagination and search parameters.
   *
   * @param {PaginationPropsWithSearch} params - The pagination and search parameters.
   * @returns {Promise<CommonEntitiesListResponse<SurgeryResponseDto>>} A promise that resolves with a response containing a list of surgeries.
   */
  getSurgeries: (params: PaginationPropsWithSearch) =>
    axiosInstance.get<CommonEntitiesListResponse<SurgeryResponseDto>>(
      '/medical-background/surgeries',
      { params },
    ),

  /**
   * Retrieves a list of symptoms based on pagination and search parameters.
   *
   * @param {PaginationPropsWithSearch} params - The pagination and search parameters.
   * @returns {Promise<CommonEntitiesListResponse<SymptomResponseDto>>} A promise that resolves with a response containing a list of symptoms.
   */
  getSymptoms: (params: PaginationPropsWithSearch) =>
    axiosInstance.get<CommonEntitiesListResponse<SymptomResponseDto>>(
      '/medical-background/symptoms',
      { params },
    ),

  /**
   * Adds an allergy to a patient's medical background.
   *
   * @param {{patientId: string, allergyId: string}} param - An object containing the patient ID and the allergy ID to add.
   * @returns {Promise<void>} A promise that resolves when the operation is successful.
   */
  setPatientAllergy: ({
    patientId,
    allergyId,
  }: {
    patientId: string;
    allergyId: string;
  }) =>
    axiosInstance.post(
      `/medical-background/patients/${patientId}/allergies/${allergyId}`,
    ),

  /**
   * Removes an allergy from a patient's medical background.
   *
   * @param {{patientId: string, allergyId: string}} param - An object containing the patient ID and the allergy ID to remove.
   * @returns {Promise<void>} A promise that resolves when the operation is successful.
   */
  removePatientAllergy: ({
    patientId,
    allergyId,
  }: {
    patientId: string;
    allergyId: string;
  }) =>
    axiosInstance.delete(
      `/medical-background/patients/${patientId}/allergies/${allergyId}`,
    ),

  /**
   * Adds a disease to a patient's medical background.
   *
   * @param {{patientId: string, diseaseId: string}} param - An object containing the patient ID and the disease ID to add.
   * @returns {Promise<void>} A promise that resolves when the operation is successful.
   */
  setPatientDisease: ({
    patientId,
    diseaseId,
  }: {
    patientId: string;
    diseaseId: string;
  }) =>
    axiosInstance.post(
      `/medical-background/patients/${patientId}/diseases/${diseaseId}`,
    ),

  /**
   * Removes a disease from a patient's medical background.
   *
   * @param {{patientId: string, diseaseId: string}} param - An object containing the patient ID and the disease ID to remove.
   * @returns {Promise<void>} A promise that resolves when the operation is successful.
   */
  removePatientDisease: ({
    patientId,
    diseaseId,
  }: {
    patientId: string;
    diseaseId: string;
  }) =>
    axiosInstance.delete(
      `/medical-background/patients/${patientId}/diseases/${diseaseId}`,
    ),

  /**
   * Saves consultation data for a given appointment ID.
   *
   * @param {string} appointmentId - The ID of the appointment to save consultation data for.
   * @param {AppointmentDataCreationDto} data - The data to save.
   * @returns {Promise<AppointmentDataResponseDto>} A promise that resolves with the updated consultation data response.
   */
  saveConsultationData: (
    appointmentId: string,
    data: AppointmentDataCreationDto,
  ) =>
    axiosInstance.put<AppointmentDataResponseDto>(
      `/appointment-data/${appointmentId}`,
      data,
    ),

  /**
   * Sets patient family historic data by patient ID and family history creation data.
   *
   * @param {string} patientId - The ID of the patient.
   * @param {FamilyHistoryCreationDto} data - The family history creation data to set.
   * @returns {Promise<FamilyHistoryResponseDto>} A promise that resolves with the created family history response.
   */
  setPatientFamilyHistoricData: (
    patientId: string,
    data: FamilyHistoryCreationDto,
  ) =>
    axiosInstance.post<FamilyHistoryResponseDto>(
      `/medical-background/patients/${patientId}/family-history`,
      data,
    ),

  /**
   * Updates patient family historic data by patient ID, family history ID and family history creation data.
   *
   * @param {string} patientId - The ID of the patient.
   * @param {string} familyHistoryId - The ID of the family history to update.
   * @param {FamilyHistoryCreationDto} data - The updated family history creation data.
   * @returns {Promise<FamilyHistoryResponseDto>} A promise that resolves with the updated family history response.
   */
  updatePatientFamilyHistoricData: (
    patientId: string,
    familyHistoryId: string,
    data: FamilyHistoryCreationDto,
  ) =>
    axiosInstance.patch<FamilyHistoryResponseDto>(
      `/medical-background/patients/${patientId}/family-history/${familyHistoryId}`,
      data,
    ),

  /**
   * Sets patient surgery data by patient ID and surgery creation data.
   *
   * @param {string} patientId - The ID of the patient.
   * @param {SurgeryCreationDto} data - The surgery creation data to set.
   * @returns {Promise<SurgeryDataResponseDto>} A promise that resolves with the created surgery response.
   */
  setPatientSurgeryData: (patientId: string, data: SurgeryCreationDto) =>
    axiosInstance.post<SurgeryDataResponseDto>(
      `/medical-background/patients/${patientId}/surgeries`,
      data,
    ),

  /**
   * Updates patient surgery data by patient ID, surgery data ID and surgery creation data.
   *
   * @param {string} patientId - The ID of the patient.
   * @param {string} surgeryDataId - The ID of the surgery data to update.
   * @param {SurgeryCreationDto} data - The updated surgery creation data.
   * @returns {Promise<SurgeryDataResponseDto>} A promise that resolves with the updated surgery response.
   */
  updatePatientSurgeryData: (
    patientId: string,
    surgeryDataId: string,
    data: SurgeryCreationDto,
  ) =>
    axiosInstance.patch<SurgeryDataResponseDto>(
      `/medical-background/patients/${patientId}/surgeries/${surgeryDataId}`,
      data,
    ),

  /**
   * Deletes a family historic data for a patient by patient ID and family history ID.
   *
   * @param {string} patientId - The ID of the patient.
   * @param {string} familyHistoryId - The ID of the family history to delete.
   * @returns {Promise<FamilyHistoryResponseDto>} A promise that resolves with the deleted family history response.
   */
  deletePatientFamilyHistoricData: (
    patientId: string,
    familyHistoryId: string,
  ) =>
    axiosInstance.delete<FamilyHistoryResponseDto>(
      `/medical-background/patients/${patientId}/family-history/${familyHistoryId}`,
    ),

  /**
   * Deletes surgery data for a patient by patient ID and surgery data ID.
   *
   * @param {string} patientId - The ID of the patient.
   * @param {string} surgeryDataId - The ID of the surgery data to delete.
   * @returns {Promise<FamilyHistoryResponseDto>} A promise that resolves with the deleted surgery response.
   */
  deleteSurgeryData: (patientId: string, surgeryDataId: string) =>
    axiosInstance.delete<FamilyHistoryResponseDto>(
      `/medical-background/patients/${patientId}/surgeries/${surgeryDataId}`,
    ),

  /**
   * Retrieves health metrics for a specialization by specialization ID.
   *
   * @param {string} specializationId - The ID of the specialization to retrieve health metrics for.
   * @returns {Promise<CommonEntitiesListResponse<HealthMetricResponse>>} A promise that resolves with a response containing a list of health metrics.
   */
  getHealthMetricsBySpecialization: (specializationId: string) =>
    axiosInstance.get<CommonEntitiesListResponse<HealthMetricResponse>>(
      `/health-metrics/for-specialization/${specializationId}`,
    ),
};
